import { Chip } from '@saay/blocks';
import { Badge } from '@saay/shadcn/components/badge';
import { Button } from '@saay/shadcn/components/button';
import { Input } from '@saay/shadcn/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@saay/shadcn/components/select';
import { ToggleGroup } from '@saay/shadcn/components/toggle-group';
import { XIcon } from 'lucide-react';
import { type ComponentProps, type ReactNode } from 'react';

const ALL = '__all__';

export interface ListFilterOption {
  readonly label: string;
  readonly value: string;
}

export interface ListFilterChip {
  readonly id: string;
  readonly label: ReactNode;
  readonly onRemove: () => void;
}

/**
 * The removable chips of the applied filters, under a list's `FilterBar` (`@saay/blocks`), with
 * «پاک‌کردن همه» when the screen offers it.
 */
export function ActiveFilterChips({
  chips,
  onClearAll,
}: {
  readonly chips: readonly ListFilterChip[];
  readonly onClearAll?: (() => void) | undefined;
}) {
  if (chips.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-2" role="list">
      {chips.map((chip) => (
        <Badge className="gap-1" key={chip.id} role="listitem" variant="secondary">
          <span>{chip.label}</span>
          <button
            aria-label={`حذف فیلتر ${chip.id}`}
            className="inline-flex"
            onClick={chip.onRemove}
            type="button"
          >
            <XIcon aria-hidden="true" className="size-3" />
          </button>
        </Badge>
      ))}
      {onClearAll === undefined ? null : (
        <Button onClick={onClearAll} size="sm" type="button" variant="ghost">
          پاک‌کردن همه
        </Button>
      )}
    </div>
  );
}

export function ListSearchInput({
  invalid = false,
  label,
  ...rest
}: {
  readonly invalid?: boolean;
  readonly label: string;
} & Omit<ComponentProps<typeof Input>, 'type' | 'aria-label'>) {
  return (
    <Input
      {...rest}
      aria-invalid={invalid || undefined}
      aria-label={label}
      className="w-full min-w-0 rounded-full bg-background"
      enterKeyHint="search"
      spellCheck={false}
      type="search"
    />
  );
}

/** A list filter. Empty value means the placeholder («همه …»). */
export function ListSelect({
  disabled = false,
  label,
  onChange,
  options,
  placeholder,
  value,
}: {
  readonly disabled?: boolean;
  readonly label: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly ListFilterOption[];
  readonly placeholder: string;
  readonly value: string;
}) {
  return (
    <Select
      disabled={disabled}
      onValueChange={(next) => onChange(next === null || next === ALL ? '' : next)}
      value={value === '' ? ALL : value}
    >
      <SelectTrigger
        aria-label={label}
        className="w-full min-w-0 rounded-full bg-background sm:w-auto sm:min-w-36"
      >
        <SelectValue placeholder={placeholder}>
          {(selected: string | null) => {
            if (selected == null || selected === ALL) return placeholder;
            return options.find((option) => option.value === selected)?.label ?? selected;
          }}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * A single-choice set of quick filters (status, view) as chips, for the `quickFilters` line of a
 * `FilterBar`. Choosing the selected chip again keeps it.
 */
export function ListToggleGroup<K extends string>({
  label,
  onChange,
  options,
  value,
}: {
  readonly label: string;
  readonly onChange: (key: K) => void;
  readonly options: readonly { readonly key: K; readonly label: string }[];
  readonly value: K | undefined;
}) {
  return (
    <ToggleGroup
      aria-label={label}
      className="gap-2"
      onValueChange={(next) => {
        const values = Array.isArray(next) ? next : next == null || next === '' ? [] : [next];
        const picked = values.find((item) => item !== value);
        if (typeof picked === 'string') onChange(picked as K);
      }}
      value={value === undefined ? [] : [value]}
    >
      {options.map((option) => (
        <Chip key={option.key} value={option.key}>
          {option.label}
        </Chip>
      ))}
    </ToggleGroup>
  );
}
